import React, { useEffect, useMemo } from 'react';
import { JobType } from '../graphql/types';
import type { Document } from '../graphql/types';
import { DocumentRail } from './DocumentRail';
import { LoanRail, RAIL_HEADING_CLASS } from './LoanRail';
import { RailItem } from './Rail';
import { resolveExtractSelection, summarizeLoanFields } from './extract/extractModel';
import { useWorkflowWorkspace } from './WorkflowWorkspace';

/**
 * WorkflowRail: the one Rail of a DD/BA Workspace, the same in Intake, Extract
 * and Analyze (Requirement 3.1). DD lists the loans ("Loans · 3 need you"); BA
 * lists the loan's standard documents ("Documents · 1 missing").
 *
 * It reads the loans, files and documents the Workspace loaded once
 * (`data`), so the Mode bodies do not carry a Rail of their own. Selection is
 * the URL's `loan` / `doc` / `field`: choosing a row rewrites those keys and
 * leaves `mode` alone, so the selection survives Mode changes (Requirement 3.4).
 *
 * - Intake and Analyze: a chosen loan sets `loan` and keeps the Mode.
 * - Extract: the loan shown is the one Extract resolved from the URL (the first
 *   loan with extracted documents by default); choosing a loan switches the file.
 * - Analyze (DD): an "All loans" row above the loans clears `loan`, so the
 *   job-wide (board `3a`) and single-loan (board `3b`) views stay reachable.
 *
 * It also reports what it knows to the Top_Bar: field counts, and for BA the
 * borrower and loan number. Intake reports its own file and document counts,
 * because only Intake knows about files that are added but not yet extracted.
 */

const MESSAGE = 'px-4 py-3 text-sm text-gray-500';

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

export const WorkflowRail: React.FC = () => {
  const {
    job,
    jobType,
    mode,
    selection,
    select,
    data,
    refreshData,
    confirmationsVersion,
    analysis,
    reportStatus,
  } = useWorkflowWorkspace();
  const jobId = job.jobId;
  const isBA = jobType === JobType.BA;
  const loaded = data.status === 'ready' ? data.data : null;

  const documentsByLoan = useMemo(() => {
    const byLoan: Record<string, Document[]> = {};
    for (const doc of loaded?.documents ?? []) (byLoan[doc.loanId] ??= []).push(doc);
    return byLoan;
  }, [loaded]);

  // Field counts per loan, using the confirmations saved for it.
  const summaries = useMemo(() => {
    const result: Record<string, ReturnType<typeof summarizeLoanFields>> = {};
    for (const [loanId, docs] of Object.entries(documentsByLoan)) {
      result[loanId] = summarizeLoanFields(jobId, loanId, docs);
    }
    return result;
    // confirmationsVersion: re-read the saved confirmations after Extract changes them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, documentsByLoan, confirmationsVersion]);

  const lowConfidenceCounts = useMemo(
    () => Object.fromEntries(Object.entries(summaries).map(([id, s]) => [id, s.lowConfidence])),
    [summaries]
  );
  const totalFields = useMemo(
    () => Object.values(summaries).reduce((n, s) => n + s.total, 0),
    [summaries]
  );
  const confirmedFields = useMemo(
    () => Object.values(summaries).reduce((n, s) => n + s.confirmed, 0),
    [summaries]
  );

  // The loan Extract resolves from the URL; the same one in every Mode for BA.
  const resolved = useMemo(
    () =>
      loaded
        ? resolveExtractSelection(loaded, {
            loan: selection.loan,
            doc: selection.doc,
            field: selection.field,
          })
        : null,
    [loaded, selection.loan, selection.doc, selection.field]
  );

  // ── Counts for the Top_Bar ────────────────────────────────────────────────
  const firstLoan = loaded?.loans[0];
  const fileCount = loaded?.files.length;
  useEffect(() => {
    if (!loaded) return;
    reportStatus({
      ...(mode !== 'intake' && !isBA && fileCount ? { fileCount } : {}),
      ...(totalFields > 0 ? { confirmedFields, totalFields } : {}),
      ...(isBA && firstLoan
        ? {
            borrowerName: firstLoan.borrowerName?.trim() || undefined,
            loanNumber: firstLoan.loanNumber?.trim() || undefined,
          }
        : {}),
    });
  }, [loaded, mode, isBA, fileCount, totalFields, confirmedFields, firstLoan, reportStatus]);

  // ── States ────────────────────────────────────────────────────────────────
  const heading = isBA ? 'Documents' : 'Loans';
  if (data.status === 'loading') {
    return (
      <div data-testid={isBA ? 'document-rail' : 'loan-rail'}>
        <p className={RAIL_HEADING_CLASS}>
          <span>{heading}</span>
        </p>
        <p role="status" className={MESSAGE}>
          Loading…
        </p>
      </div>
    );
  }
  if (data.status === 'error' || !loaded || !resolved) {
    return (
      <div data-testid={isBA ? 'document-rail' : 'loan-rail'}>
        <p className={RAIL_HEADING_CLASS}>
          <span>{heading}</span>
        </p>
        <div role="alert" className={MESSAGE}>
          <p>The {isBA ? 'documents' : 'loans'} could not be loaded.</p>
          <button
            type="button"
            onClick={() => void refreshData(true)}
            className="mt-2 rounded text-sm font-bold text-[#5A21C9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (isBA) {
    return (
      <DocumentRail
        documents={resolved.loanDocuments}
        jobId={jobId}
        loanId={resolved.loanId ?? undefined}
      />
    );
  }

  // DD. Extract shows the loan it resolved; Intake and Analyze show the URL's loan.
  const urlLoanId =
    selection.loan && loaded.loans.some((l) => l.loanId === selection.loan) ? selection.loan : null;
  const selectedLoanId = mode === 'extract' ? resolved.loanId : urlLoanId;

  const allLoansRow =
    mode === 'analyze' && loaded.loans.length > 0 ? (
      <RailItem
        selected={selectedLoanId === null}
        onClick={() => select({ loan: null, doc: null, field: null, exception: null })}
      >
        <span className="block font-bold text-gray-900">All loans</span>
        <span className="block text-xs text-gray-500" data-testid="all-loans-meta">
          {plural(loaded.loans.length, 'loan')}
          {analysis
            ? ` · ${plural(analysis.findings.filter((f) => f.status !== 'PASS').length, 'finding')}`
            : ''}
        </span>
      </RailItem>
    ) : undefined;

  return (
    <LoanRail
      loans={loaded.loans}
      jobId={jobId}
      documentsByLoan={documentsByLoan}
      analysis={analysis}
      lowConfidenceCounts={lowConfidenceCounts}
      selectedLoanId={selectedLoanId}
      leading={allLoansRow}
    />
  );
};

export default WorkflowRail;
